import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Save, Sparkles, AlertCircle, CheckCircle2, Info, Eye, EyeOff, Zap } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getAppToken } from '@/lib/oauthClient';

const PROVIDERS = [
  { 
    id: 'openai', 
    name: 'OpenAI', 
    logo: '🤖',
    models: [
      { id: 'gpt-5.4-mini', name: 'GPT-5.4-mini', cost: '$0.0015/ticket', recommended: true },
      { id: 'gpt-5.4', name: 'GPT-5.4', cost: '$0.0060/ticket' }
    ],
    keyField: 'openai_api_key',
    keyLabel: 'Clé API OpenAI',
    keyPlaceholder: 'sk-proj-...'
  },
  { 
    id: 'anthropic', 
    name: 'Anthropic (Claude)', 
    logo: '🧠',
    models: [
      { id: 'claude-sonnet-4-6', name: 'Claude Sonnet 4.6', cost: '$0.0060/ticket', recommended: true },
      { id: 'claude-opus-4-7', name: 'Claude Opus 4.7', cost: '$0.0300/ticket' }
    ],
    keyField: 'anthropic_api_key',
    keyLabel: 'Clé API Anthropic',
    keyPlaceholder: 'sk-ant-api03-...'
  },
  { 
    id: 'gemini', 
    name: 'Google (Gemini)', 
    logo: '✨',
    models: [
      { id: 'gemini-3.1-pro-preview', name: 'Gemini 3.1 Pro', cost: '$0.0020/ticket', recommended: true },
      { id: 'gemini-3-flash-preview', name: 'Gemini 3 Flash', cost: '$0.0003/ticket' },
      { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', cost: '$0.0003/ticket' }
    ],
    keyField: 'google_api_key',
    keyLabel: 'Clé API Google',
    keyPlaceholder: 'AIza...'
  }
];

const CATEGORIES = [
  { id: 'account_access', label: 'Accès compte', color: 'bg-blue-500' },
  { id: 'payment', label: 'Paiement', color: 'bg-green-500' },
  { id: 'contract', label: 'Contrat', color: 'bg-purple-500' },
  { id: 'technical', label: 'Technique', color: 'bg-red-500' },
  { id: 'other', label: 'Autre', color: 'bg-gray-500' }
];

const PRIORITIES = [
  { id: 'low', label: 'Basse', color: 'bg-gray-400' },
  { id: 'medium', label: 'Moyenne', color: 'bg-yellow-500' },
  { id: 'high', label: 'Haute', color: 'bg-orange-500' },
  { id: 'urgent', label: 'Urgente', color: 'bg-red-600' }
];

export default function AISettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  
  // Local state for form
  const [enabled, setEnabled] = useState(true);
  const [minConfidence, setMinConfidence] = useState(90);
  const [allowedCategories, setAllowedCategories] = useState([]);
  const [allowedPriorities, setAllowedPriorities] = useState([]);
  
  // Provider & Model state
  const [aiProvider, setAiProvider] = useState('openai');
  const [aiModel, setAiModel] = useState('gpt-5.4-mini');
  
  // API Keys state
  const [emergentKey, setEmergentKey] = useState('');
  const [openaiKey, setOpenaiKey] = useState('');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [googleKey, setGoogleKey] = useState('');
  
  // Show/hide keys
  const [showKeys, setShowKeys] = useState({
    emergent: false,
    openai: false,
    anthropic: false,
    google: false
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const appToken = await getAppToken();
      const res = await axios.get(`${backendUrl}/api/admin/ai-settings`, {
        headers: { 'X-App-Token': appToken }
      });
      
      const data = res.data.settings;
      setSettings(data);
      setEnabled(data.enabled);
      setMinConfidence(data.min_confidence * 100);
      setAllowedCategories(data.allowed_categories || []);
      setAllowedPriorities(data.allowed_priorities || []);
      setAiProvider(data.ai_provider || 'openai');
      setAiModel(data.ai_model || 'gpt-5.4-mini');
      
      // Keys are masked from API (****xxxx)
      setEmergentKey(data.emergent_llm_key || '');
      setOpenaiKey(data.openai_api_key || '');
      setAnthropicKey(data.anthropic_api_key || '');
      setGoogleKey(data.google_api_key || '');
    } catch (error) {
      console.error('Erreur chargement settings:', error);
      toast.error('Erreur lors du chargement de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const appToken = await getAppToken();
      
      const payload = {
        enabled,
        min_confidence: minConfidence / 100,
        allowed_categories: allowedCategories,
        allowed_priorities: allowedPriorities,
        ai_provider: aiProvider,
        ai_model: aiModel
      };
      
      // Only send keys if they're not masked (don't start with ****)
      if (emergentKey && !emergentKey.startsWith('****')) {
        payload.emergent_llm_key = emergentKey;
      }
      if (openaiKey && !openaiKey.startsWith('****')) {
        payload.openai_api_key = openaiKey;
      }
      if (anthropicKey && !anthropicKey.startsWith('****')) {
        payload.anthropic_api_key = anthropicKey;
      }
      if (googleKey && !googleKey.startsWith('****')) {
        payload.google_api_key = googleKey;
      }
      
      await axios.put(`${backendUrl}/api/admin/ai-settings`, payload, {
        headers: { 'X-App-Token': appToken }
      });
      
      toast.success('Configuration IA enregistrée avec succès');
      fetchSettings(); // Refresh
    } catch (error) {
      console.error('Erreur sauvegarde settings:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setAllowedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  const togglePriority = (priorityId) => {
    setAllowedPriorities(prev =>
      prev.includes(priorityId)
        ? prev.filter(p => p !== priorityId)
        : [...prev, priorityId]
    );
  };
  
  const handleProviderChange = (providerId) => {
    setAiProvider(providerId);
    // Set default model for provider
    const provider = PROVIDERS.find(p => p.id === providerId);
    if (provider && provider.models.length > 0) {
      const recommended = provider.models.find(m => m.recommended);
      setAiModel(recommended ? recommended.id : provider.models[0].id);
    }
  };
  
  const getCurrentProvider = () => PROVIDERS.find(p => p.id === aiProvider);
  const getAvailableModels = () => getCurrentProvider()?.models || [];

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const currentProvider = getCurrentProvider();
  const currentModel = currentProvider?.models.find(m => m.id === aiModel);

  return (
    <div className="space-y-6 p-6" data-testid="ai-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-purple-600" />
            Configuration IA
          </h1>
          <p className="text-muted-foreground mt-1">
            Gérez le provider, le modèle et les règles d'auto-réponse de l'assistant intelligent
          </p>
        </div>
        
        <Button
          onClick={handleSave}
          disabled={saving}
          className="gap-2"
          data-testid="save-settings-button"
        >
          <Save className="h-4 w-4" />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>

      {/* Current Config Badge */}
      <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <Zap className="h-5 w-5 text-purple-600" />
            <div className="text-sm">
              <strong>Configuration actuelle :</strong>{' '}
              <Badge variant="secondary" className="ml-2">{currentProvider?.logo} {currentProvider?.name}</Badge>
              <Badge variant="outline" className="ml-2">{currentModel?.name}</Badge>
              <span className="ml-2 text-muted-foreground">({currentModel?.cost})</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Provider Selection */}
      <Card data-testid="provider-card">
        <CardHeader>
          <CardTitle>Provider IA</CardTitle>
          <CardDescription>
            Choisissez le fournisseur d'IA pour l'auto-réponse
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PROVIDERS.map(provider => {
              const isActive = aiProvider === provider.id;
              return (
                <button
                  key={provider.id}
                  onClick={() => handleProviderChange(provider.id)}
                  className={`
                    p-4 rounded-lg border-2 transition-all text-left
                    ${isActive
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/30'
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                    }
                  `}
                  data-testid={`provider-${provider.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{provider.logo}</span>
                      <span className="font-bold">{provider.name}</span>
                    </div>
                    {isActive && <CheckCircle2 className="h-5 w-5 text-purple-600" />}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {provider.models.length} modèle{provider.models.length > 1 ? 's' : ''} disponible{provider.models.length > 1 ? 's' : ''}
                  </p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Model Selection */}
      <Card data-testid="model-card">
        <CardHeader>
          <CardTitle>Modèle IA</CardTitle>
          <CardDescription>
            Sélectionnez le modèle à utiliser avec {currentProvider?.name}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {getAvailableModels().map(model => {
              const isActive = aiModel === model.id;
              return (
                <button
                  key={model.id}
                  onClick={() => setAiModel(model.id)}
                  className={`
                    w-full p-4 rounded-lg border-2 transition-all text-left flex items-center justify-between
                    ${isActive
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/30'
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                    }
                  `}
                  data-testid={`model-${model.id}`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{model.name}</span>
                      {model.recommended && <Badge className="bg-green-600 text-xs">Recommandé</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{model.cost}</p>
                  </div>
                  {isActive && <CheckCircle2 className="h-5 w-5 text-purple-600" />}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* API Keys Section */}
      <Card data-testid="api-keys-card">
        <CardHeader>
          <CardTitle>Clés API</CardTitle>
          <CardDescription>
            Configurez vos clés API (la clé Emergent universelle fonctionne pour tous les providers)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Emergent Universal Key */}
          <div>
            <Label htmlFor="emergent-key" className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-600" />
              Clé Universelle Emergent (Recommandé)
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              Une seule clé pour OpenAI, Anthropic et Google
            </p>
            <div className="flex gap-2">
              <Input
                id="emergent-key"
                type={showKeys.emergent ? "text" : "password"}
                value={emergentKey}
                onChange={(e) => setEmergentKey(e.target.value)}
                placeholder="sk-emergent-..."
                className="flex-1"
                data-testid="emergent-key-input"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setShowKeys({...showKeys, emergent: !showKeys.emergent})}
              >
                {showKeys.emergent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <Separator />

          {/* Provider-specific keys */}
          <div className="space-y-4">
            <p className="text-sm font-medium">Clés spécifiques par provider (optionnel)</p>
            
            {PROVIDERS.map(provider => (
              <div key={provider.id}>
                <Label htmlFor={`${provider.id}-key`} className="flex items-center gap-2">
                  <span>{provider.logo}</span>
                  {provider.keyLabel}
                </Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    id={`${provider.id}-key`}
                    type={showKeys[provider.id] ? "text" : "password"}
                    value={
                      provider.id === 'openai' ? openaiKey :
                      provider.id === 'anthropic' ? anthropicKey :
                      googleKey
                    }
                    onChange={(e) => {
                      if (provider.id === 'openai') setOpenaiKey(e.target.value);
                      else if (provider.id === 'anthropic') setAnthropicKey(e.target.value);
                      else setGoogleKey(e.target.value);
                    }}
                    placeholder={provider.keyPlaceholder}
                    className="flex-1"
                    data-testid={`${provider.id}-key-input`}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setShowKeys({...showKeys, [provider.id]: !showKeys[provider.id]})}
                  >
                    {showKeys[provider.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-blue-50 dark:bg-blue-950/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800 text-sm">
            <p className="font-semibold text-blue-900 dark:text-blue-100">💡 Priorité des clés</p>
            <p className="text-blue-800 dark:text-blue-200 mt-1">
              1. Clé spécifique du provider sélectionné<br />
              2. Clé universelle Emergent (si pas de clé spécifique)<br />
              3. Variable d'environnement (fallback)
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Info Banner */}
      <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900 dark:text-blue-100">
              <p className="font-semibold mb-1">Fonctionnement de l'auto-réponse IA</p>
              <p>
                Lorsqu'un utilisateur crée un ticket, l'IA analyse automatiquement la demande avec le provider et modèle configurés ci-dessus.
                Si <strong>tous les critères ci-dessous</strong> sont remplis, l'IA répond automatiquement
                et marque le ticket comme résolu. L'utilisateur peut rouvrir le ticket s'il n'est pas satisfait.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Global Enable */}
      <Card data-testid="global-enable-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {enabled ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <AlertCircle className="h-5 w-5 text-gray-400" />
            )}
            Activation globale
          </CardTitle>
          <CardDescription>
            Activer ou désactiver complètement l'auto-réponse IA
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="global-enable" className="text-base font-medium">
                Auto-réponse IA {enabled ? 'activée' : 'désactivée'}
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                {enabled
                  ? 'L\'IA répondra automatiquement aux tickets selon les critères définis'
                  : 'Tous les tickets seront traités manuellement par le support'}
              </p>
            </div>
            <Switch
              id="global-enable"
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="global-enable-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Confidence Threshold */}
      <Card data-testid="confidence-card">
        <CardHeader>
          <CardTitle>Seuil de confiance minimum</CardTitle>
          <CardDescription>
            L'IA ne répondra automatiquement que si sa confiance dépasse ce seuil
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Confiance minimum requise</Label>
            <Badge variant="secondary" className="text-lg font-bold">
              {minConfidence}%
            </Badge>
          </div>
          <Slider
            value={[minConfidence]}
            onValueChange={(value) => setMinConfidence(value[0])}
            min={70}
            max={100}
            step={5}
            className="w-full"
            data-testid="confidence-slider"
          />
          <p className="text-xs text-muted-foreground">
            Recommandé : 90% (équilibre sécurité/performance) • 80-85% = plus de réponses auto
          </p>
        </CardContent>
      </Card>

      {/* Allowed Categories */}
      <Card data-testid="categories-card">
        <CardHeader>
          <CardTitle>Catégories autorisées</CardTitle>
          <CardDescription>
            Sélectionnez les catégories de tickets pour lesquelles l'IA peut répondre automatiquement
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {CATEGORIES.map(category => {
              const isActive = allowedCategories.includes(category.id);
              return (
                <button
                  key={category.id}
                  onClick={() => toggleCategory(category.id)}
                  className={`
                    p-4 rounded-lg border-2 transition-all text-left
                    ${isActive
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/30'
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                    }
                  `}
                  data-testid={`category-${category.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${category.color}`} />
                      <span className="font-medium">{category.label}</span>
                    </div>
                    {isActive && <CheckCircle2 className="h-5 w-5 text-purple-600" />}
                  </div>
                </button>
              );
            })}
          </div>
          <Separator className="my-4" />
          <p className="text-sm text-muted-foreground">
            {allowedCategories.length} catégorie(s) sélectionnée(s)
          </p>
        </CardContent>
      </Card>

      {/* Allowed Priorities */}
      <Card data-testid="priorities-card">
        <CardHeader>
          <CardTitle>Priorités autorisées</CardTitle>
          <CardDescription>
            Sélectionnez les niveaux de priorité pour lesquels l'IA peut répondre automatiquement
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {PRIORITIES.map(priority => {
              const isActive = allowedPriorities.includes(priority.id);
              return (
                <button
                  key={priority.id}
                  onClick={() => togglePriority(priority.id)}
                  className={`
                    p-4 rounded-lg border-2 transition-all text-left
                    ${isActive
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/30'
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                    }
                  `}
                  data-testid={`priority-${priority.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className={`w-3 h-3 rounded-full ${priority.color}`} />
                    {isActive && <CheckCircle2 className="h-5 w-5 text-purple-600" />}
                  </div>
                  <span className="font-medium">{priority.label}</span>
                </button>
              );
            })}
          </div>
          <Separator className="my-4" />
          <p className="text-sm text-muted-foreground">
            {allowedPriorities.length} priorité(s) sélectionnée(s)
          </p>
          <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <p className="text-sm text-yellow-900 dark:text-yellow-100">
              ⚠️ <strong>Attention :</strong> Autoriser les priorités "Haute" ou "Urgente" peut entraîner
              des réponses automatiques sur des problèmes critiques. Assurez-vous que le seuil de confiance est élevé (≥ 95%).
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            Résumé de la configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>
            <strong>Statut :</strong> {enabled ? (
              <Badge variant="default" className="bg-green-600">Activé</Badge>
            ) : (
              <Badge variant="secondary">Désactivé</Badge>
            )}
          </p>
          <p><strong>Seuil de confiance :</strong> {minConfidence}%</p>
          <p><strong>Catégories autorisées :</strong> {allowedCategories.length > 0 ? allowedCategories.join(', ') : 'Aucune'}</p>
          <p><strong>Priorités autorisées :</strong> {allowedPriorities.length > 0 ? allowedPriorities.join(', ') : 'Aucune'}</p>
          
          {settings?.updated_by && (
            <p className="text-xs text-muted-foreground pt-2 border-t">
              Dernière modification par {settings.updated_by} le{' '}
              {new Date(settings.updated_at).toLocaleString('fr-FR')}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
